import React from "react";
import {
  Card,
  Typography,
  Box,
  Avatar,
  useTheme,
  keyframes,
} from "@mui/material";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";

type Props = {
  className?: string;
  title: string;
  description: string;
  icon?: React.ReactNode;
  subtitle?: string;
};

const pulse = keyframes`
  0% { transform: scale(1); }
  50% { transform: scale(1.06); }
  100% { transform: scale(1); }
`;

const DashboardCard: React.FC<Props> = ({
  className = "",
  title,
  description,
  icon = <TrendingUpIcon fontSize="large" />,
  subtitle,
}) => {
  const theme = useTheme();

  return (
    <Card
      className={className}
      elevation={0}
      sx={{
        p: 4,
        borderRadius: 5,
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        background: "rgba(255, 255, 255, 0.9)",
        boxShadow: "0 4px 24px rgba(0, 0, 0, 0.08)",
        backdropFilter: "blur(12px)",
        transition: "transform 0.3s ease, box-shadow 0.3s ease",
        "&:hover": {
          transform: "translateY(-4px)",
          boxShadow: "0 8px 32px rgba(0, 0, 0, 0.15)",
        },
      }}
    >
      {/* Left Text */}
      <Box>
        <Typography
          variant="overline"
          sx={{
            color: theme.palette.text.secondary,
            fontWeight: 600,
            letterSpacing: 1,
          }}
        >
          {title}
        </Typography>

        <Typography
          variant="h4"
          sx={{
            fontWeight: 800,
            mt: 1,
            color: theme.palette.text.primary,
          }}
        >
          {description}
        </Typography>

        {subtitle && (
          <Typography variant="body2" color="text.secondary" mt={1}>
            {subtitle}
          </Typography>
        )}
      </Box>

      {/* Right Icon */}
      <Box
        sx={{
          width: 72,
          height: 72,
          borderRadius: "50%",
          background: "linear-gradient(135deg, #42a5f5, #7e57c2)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          animation: `${pulse} 3s infinite`,
          boxShadow: "0 0 0 4px rgba(66, 165, 245, 0.2)",
        }}
      >
        <Avatar
          sx={{
            bgcolor: "#fff",
            color: theme.palette.primary.main,
            width: 56,
            height: 56,
            boxShadow: "0 2px 12px rgba(0, 0, 0, 0.1)",
          }}
        >
          {icon}
        </Avatar>
      </Box>
    </Card>
  );
};

export default DashboardCard;
